/**
 * StudentPaymentsTab.jsx
 * Kullanicinin taksitlerini ve odeme gecmisini gosterir.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { EmptyState, StatusBadge } from '../../components/admin';
import { fmtMoneyAccounting } from '../../utils/money';
import { statusBadgeProps } from './AccountingTab/columnDefs';
import { CreditCard, CalendarClock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_STATUS_VARIANT = {
  paid: 'success',
  completed: 'success',
  pending: 'warning',
  failed: 'danger',
  refunded: 'info',
  cancelled: 'neutral',
};

// Tur 4 (elle test bulgusu) — ham `pay.status` değeri ("completed" vb.)
// doğrudan rozet etiketi olarak gösteriliyordu (i18n atlanmış). Değer kümesi
// backend `payments.status`'ün fiilen ürettiği değerlerle sınırlı (bkz.
// routes/payments.py: completed/pending/failed; refunded/cancelled ileriye
// dönük/savunma amaçlı) — bilinmeyen bir değer gelirse ham metne düşülür,
// çökmez.
const PAYMENT_STATUS_KEY = {
  paid: 'accounting.payment_status.completed',
  completed: 'accounting.payment_status.completed',
  pending: 'accounting.payment_status.pending',
  failed: 'accounting.payment_status.failed',
  refunded: 'accounting.payment_status.refunded',
  cancelled: 'accounting.payment_status.cancelled',
};

// Taksit durumu rozeti: AccountingTab/columnDefs.js'teki merkezi STATUS_CONFIG'e
// bağlıdır (Muhasebe FAZ 3 review bulgusu — bu dosya kendi paralel sözlüğünü
// taşıyordu, ledger sekmesiyle çakışan renk/etiket üretiyordu). İkon kutusunun
// rengi de aynı `variant`'tan türetilir, bkz. INSTALLMENT_ICON_BOX_COLORS.
const INSTALLMENT_ICON_BOX_COLORS = {
  success: 'bg-green-100 text-green-600',
  warning: 'bg-amber-100 text-amber-600',
  danger: 'bg-rose-100 text-rose-600',
  info: 'bg-sky-100 text-sky-600',
  purple: 'bg-purple-100 text-purple-600',
  primary: 'bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]',
  neutral: 'bg-gray-100 text-gray-600',
};

export default function StudentPaymentsTab({ detail }) {
  const { lang, t } = useLanguage();

  const payments = detail?.payments || [];
  const installments = detail?.installments || [];

  // course_id -> title map — payments'ta ham kurs slug'i (ör. course_cabin_main_v1)
  // sizmasin diye (bkz. onaylanan satis planinda ayni sorun, AdminSatisOnay.jsx).
  const [coursesMap, setCoursesMap] = useState({});
  useEffect(() => {
    axios.get(`${API}/courses?limit=200`)
      .then((r) => {
        const map = {};
        (r.data.courses || []).forEach((c) => { map[c.course_id] = c.title; });
        setCoursesMap(map);
      })
      .catch(() => {});
  }, []);
  const courseLabel = (pay) => {
    if (pay.course_title) return pay.course_title;
    if (!pay.course_id) return null;
    if (pay.course_id === 'multiple') return t('accounting.summary.mixed_down_payment');
    return coursesMap[pay.course_id] || pay.course_id;
  };

  // Faz 3.7a: yerel Intl.NumberFormat cekirdek `fmtMoneyAccounting`'e tasindi
  // (davranis degisikligi kabul: null/0 artik "₺0,00", "—" DEGIL, plan §11.3).
  const fmtTRY = (n) => fmtMoneyAccounting(n, lang);

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US') : '-';

  if (payments.length === 0 && installments.length === 0) {
    return (
      <EmptyState
        title={t('accounting.summary.empty_title')}
        description={t('accounting.summary.empty_description')}
        icon={<CreditCard size={40} strokeWidth={1.5} />}
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* Taksitler */}
      {installments.length > 0 && (
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--text-muted)] mb-2 flex items-center gap-1.5">
            <CalendarClock size={13} />
            {t('accounting.summary.installments_heading')} ({installments.length})
          </h3>
          <div className="space-y-2">
            {installments.map((inst, i) => {
              // installment.status backend'de `_effective_installment_status()` ile
              // YERİNDE efektifleştirilir (bkz. admin_student_full_detail) — burada
              // ayrıca bir gecikme hesabı YAPILMAZ, ham değer doğrudan tüketilir.
              // Değer kümesi ledger ile birebir aynı: pending/due/overdue/
              // overdue_warn/interest/legal/paid/cancelled.
              const { variant, label, icon } = statusBadgeProps(inst.status, t);
              return (
                <div
                  key={inst.installment_id || i}
                  className="flex items-center gap-3 p-3 rounded-xl border-2 border-[var(--border-light)] hover:bg-gray-50"
                  data-testid={`installment-row-${i}`}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      INSTALLMENT_ICON_BOX_COLORS[variant] || INSTALLMENT_ICON_BOX_COLORS.neutral
                    }`}
                  >
                    <CalendarClock size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm">{fmtTRY(inst.amount)}</p>
                    <p className="text-[10px] text-[var(--text-muted)]">
                      {t('accounting.summary.due_label')}: {fmtDate(inst.due_date)}
                      {inst.installment_no
                        ? ` · ${inst.installment_no}. ${t('accounting.summary.installment_unit')}`
                        : ''}
                    </p>
                  </div>
                  <StatusBadge variant={variant} label={label} icon={icon} dot />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Odeme gecmisi (tahsilatlar) */}
      {payments.length > 0 && (
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--text-muted)] mb-2 flex items-center gap-1.5">
            <CreditCard size={13} />
            {t('accounting.summary.payment_history_heading')} ({payments.length})
          </h3>
          <div className="space-y-2">
            {payments.map((pay, i) => (
              <div
                key={pay.payment_id || pay._id || i}
                className="flex items-center gap-3 p-3 rounded-xl border-2 border-[var(--border-light)] hover:bg-gray-50"
                data-testid={`payment-row-${i}`}
              >
                <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                  <CreditCard size={16} className="text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm">{fmtTRY(pay.amount)}</p>
                  {courseLabel(pay) && (
                    <p className="text-[10px] text-[var(--text-muted)] truncate">
                      {courseLabel(pay)}
                    </p>
                  )}
                </div>
                <StatusBadge
                  variant={PAYMENT_STATUS_VARIANT[pay.status] || 'neutral'}
                  label={PAYMENT_STATUS_KEY[pay.status] ? t(PAYMENT_STATUS_KEY[pay.status]) : (pay.status || '-')}
                  dot
                />
                {pay.created_at && (
                  <span className="text-[10px] text-[var(--text-muted)] flex-shrink-0">
                    {fmtDate(pay.created_at)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
